import React, { useCallback, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  MRT_TableInstance,
  useMaterialReactTable
} from "material-react-table";
import { useTranslation } from "react-i18next";
import { ListTopToolbar } from "../../components/Common/ListTablePartial/List-top-toolbar.partial";
import TableDeleteConfirmDialog from "../../components/Common/TableDeletionConfirmDialog/TableDeletionConfirmDialog.component";
import { commonLocalization } from "../../functions/tableLocalization";
import {
  maxStoredOrderWhenAdding,
  maxStoredOrderWhenEditing,
  nextStoredOrderForNewDocument,
  useVisitorConsentDocuments
} from "./visitor-consent-documents.context";
import type {
  VisitorConsentDocument,
  VisitorConsentDocumentUpsertDraft
} from "./visitor-consent-documents.context";
import { VisitorConsentDocumentsTableActionsCell } from "./visitor-consent-documents-table-actions-cell.component";
import { VisitorConsentDocumentsFormDialog } from "./visitor-consent-documents-form-dialog.component";
import {
  renderVisitorConsentDocumentsDescriptionCell,
  renderVisitorConsentDocumentsFileCell,
  renderVisitorConsentDocumentsOrderCell,
  renderVisitorConsentDocumentsTitleCell,
  visitorConsentDocumentsDescriptionColumnLayout
} from "./visitor-consent-documents-table.partial";
import { withSeeditMrtInitialState } from "../../themes/mrt-table-theme";
import { useSeeditMrtTableOptions } from "../../themes/use-seedit-mrt-table-options";

type DeleteDialogState =
  | { kind: "single"; doc: VisitorConsentDocument }
  | { kind: "bulk"; ids: string[] };

export const VisitorConsentDocumentsTable: React.FC = () => {
  const { t } = useTranslation();
  const {
    orderedDocuments,
    addDocument,
    updateDocument,
    deleteDocument,
    deleteDocuments,
    isLoading,
    isMutating
  } = useVisitorConsentDocuments();

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<"add" | "edit">("add");
  const [editing, setEditing] = useState<VisitorConsentDocument | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const deleteSessionRef = useRef(0);
  const closeRequestSessionRef = useRef<number | null>(null);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  const nextOrderSuggestion = useMemo(
    () => nextStoredOrderForNewDocument(orderedDocuments),
    [orderedDocuments]
  );

  const orderMaxStored = useMemo(
    () =>
      formMode === "add"
        ? maxStoredOrderWhenAdding(orderedDocuments)
        : maxStoredOrderWhenEditing(orderedDocuments),
    [formMode, orderedDocuments]
  );

  const canDeleteRow = orderedDocuments.length > 1;

  const selectedIds = useMemo(
    () =>
      Object.entries(rowSelection)
        .filter(([, selected]) => selected)
        .map(([id]) => id),
    [rowSelection]
  );
  const canBulkDelete = selectedIds.length > 0 && selectedIds.length < orderedDocuments.length;

  const openAdd = useCallback(() => {
    setFormMode("add");
    setEditing(null);
    setFormOpen(true);
  }, []);

  const openEdit = useCallback((doc: VisitorConsentDocument) => {
    setFormMode("edit");
    setEditing(doc);
    setFormOpen(true);
  }, []);

  const openDeleteModal = useCallback((next: DeleteDialogState) => {
    deleteSessionRef.current += 1;
    setDeleteDialog(next);
    setIsDeleteModalOpen(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    closeRequestSessionRef.current = deleteSessionRef.current;
    setIsDeleteModalOpen(false);
  }, []);

  const onDeleteModalExited = useCallback(() => {
    if (closeRequestSessionRef.current !== deleteSessionRef.current) {
      closeRequestSessionRef.current = null;
      return;
    }
    setDeleteDialog(null);
    closeRequestSessionRef.current = null;
  }, []);

  const handleRequestDelete = useCallback(
    (doc: VisitorConsentDocument) => {
      openDeleteModal({ kind: "single", doc });
    },
    [openDeleteModal]
  );

  const openBulkDeleteDialog = useCallback(() => {
    if (!canBulkDelete) {
      return;
    }
    openDeleteModal({ kind: "bulk", ids: selectedIds });
  }, [canBulkDelete, openDeleteModal, selectedIds]);

  const handleAdd = (draft: VisitorConsentDocumentUpsertDraft) => {
    addDocument(draft);
  };

  const handleEdit = (id: string, draft: VisitorConsentDocumentUpsertDraft) => {
    updateDocument(id, draft);
  };

  const confirmDelete = () => {
    if (!deleteDialog) {
      return;
    }
    if (deleteDialog.kind === "single") {
      deleteDocument(deleteDialog.doc.id);
    } else {
      deleteDocuments(deleteDialog.ids);
    }
    setRowSelection({});
    closeDeleteModal();
  };

  const noFileLabel = t("_visitorConsentDocuments_noFile");

  const columns = useMemo<MRT_ColumnDef<VisitorConsentDocument>[]>(
    () => [
      {
        accessorKey: "order",
        header: t("_visitorConsentDocuments_columnOrder"),
        size: 40,
        enableColumnFilter: false,
        Cell: ({ cell }) => renderVisitorConsentDocumentsOrderCell(cell.getValue<number>())
      },
      {
        accessorKey: "title",
        header: t("_visitorConsentDocuments_columnTitle"),
        size: 200,
        Cell: ({ cell }) => renderVisitorConsentDocumentsTitleCell(cell.getValue<string>())
      },
      {
        accessorKey: "description",
        header: t("_visitorConsentDocuments_columnDescription"),
        ...visitorConsentDocumentsDescriptionColumnLayout,
        Cell: ({ row }) => renderVisitorConsentDocumentsDescriptionCell(row.original.description),
        size: 350,
        enableSorting: false,
        enableColumnFilter: false
      },
      {
        id: "file",
        accessorFn: row => row.fileName ?? "",
        header: t("_visitorConsentDocuments_columnFile"),
        Cell: ({ row }) =>
          renderVisitorConsentDocumentsFileCell({
            fileName: row.original.fileName,
            emptyLabel: noFileLabel
          }),
        enableSorting: false,
        enableColumnFilter: false
      }
    ],
    [t, noFileLabel]
  );
  const seeditMrtOptions = useSeeditMrtTableOptions<VisitorConsentDocument>();

  const table = useMaterialReactTable({
    ...seeditMrtOptions,
    columns,
    data: orderedDocuments,
    localization: {
      ...commonLocalization(t),
      actions: t("_visitorConsentDocuments_actions"),
      noRecordsToDisplay: t("_visitorConsentDocuments_noRecords")
    },
    getRowId: row => row.id,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    state: {
      isLoading,
      showProgressBars: isLoading,
      rowSelection
    },
    initialState: withSeeditMrtInitialState({
      showGlobalFilter: true
    }),
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableRowActions: true,
    renderRowActions: ({ row }) => (
      <VisitorConsentDocumentsTableActionsCell
        document={row.original}
        canDelete={canDeleteRow}
        onEdit={openEdit}
        onRequestDelete={handleRequestDelete}
      />
    ),
    enableColumnActions: true,
    enableColumnFilters: false,
    enableGlobalFilter: true,
    enablePagination: false,
    enableBottomToolbar: false,
    positionToolbarAlertBanner: "none",
    renderTopToolbar: ({ table: topTable }) => (
      <ListTopToolbar
        table={topTable as unknown as MRT_TableInstance<Record<string, unknown>>}
        title={t("_visitorBookings_tabDocs")}
        createBtnLabel={t("_visitorConsentDocuments_add")}
        createButtonTestId="visitor-consent-doc-add"
        hideFilterToggle
        isEditable
        disabledDeleteButton={!canBulkDelete || isMutating}
        onCreateClick={openAdd}
        onDelete={openBulkDeleteDialog}
      />
    ),
    muiTablePaperProps: {
      elevation: 0,
      variant: "outlined",
      sx: { width: "100%" }
    },
    muiTableBodyRowProps: ({ row }) => ({
      "data-testid": `visitor-consent-doc-row-${row.original.id}`,
      hover: true
    })
  });

  return (
    <Box data-testid="visitor-consent-documents-table">
      <MaterialReactTable table={table} />

      <VisitorConsentDocumentsFormDialog
        open={formOpen}
        mode={formMode}
        document={formMode === "edit" ? editing : null}
        nextOrderSuggestion={nextOrderSuggestion}
        orderMax={orderMaxStored}
        onClose={() => setFormOpen(false)}
        onAdd={handleAdd}
        onEdit={handleEdit}
      />

      <TableDeleteConfirmDialog
        dialogTitle={
          deleteDialog?.kind === "bulk"
            ? t("_visitorConsentDocuments_deleteBulkTitle", {
                count: deleteDialog.ids.length
              })
            : deleteDialog?.kind === "single"
              ? t("_visitorConsentDocuments_deleteTitle")
              : undefined
        }
        dialogDesc={
          deleteDialog?.kind === "bulk"
            ? t("_visitorConsentDocuments_deleteBulkMessage")
            : deleteDialog?.kind === "single"
              ? t("_visitorConsentDocuments_deleteMessage")
              : ""
        }
        deleteStatus="idle"
        isDeleteModalOpen={isDeleteModalOpen}
        setIsDeleteModalOpen={nextOpen => {
          if (!nextOpen) {
            closeDeleteModal();
          }
        }}
        onDeleteModalExited={onDeleteModalExited}
        onConfirm={confirmDelete}
      />
    </Box>
  );
};
